import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { checkBetfairStatus, initiateConnection, disconnectBetfair, getAccountDetails, toggleAiMode, BetfairStatus, BetfairAccount } from '../../services/betfairService';
import { fetchTodaysPaddockSummary } from '../../services/dataService';
import { fetchSettledBets, SettledBetWithRace } from '../../services/betService';
import { fetchProfile } from '../../services/profileService';
import { CheckCircle2, Wallet, TrendingUp, TrendingDown, Percent, Hash, Target, Trophy, DollarSign, Check, X, Bot } from 'lucide-react';
import LoadingSpinner from '../LoadingSpinner';
import DashboardWelcomePanel from '../dashboard/DashboardWelcomePanel';
import './ConnectBetfairPage.css';

// Component to format and render paddock summary
const FormattedPaddockSummary: React.FC<{ text: string; expanded: boolean }> = ({ text, expanded }) => {
  const formatPaddockText = (content: string): React.ReactNode[] => {
    if (!content) return [];
    
    const parts: React.ReactNode[] = [];
    const lines = content.split('\n').filter(line => line.trim());
    
    let currentSection: string | null = null;
    let summaryLines: string[] = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Check if this is a section header (ends with colon and is short)
      if (line.endsWith(':') && line.length < 50 && !line.includes(' - ')) {
        // If we have accumulated summary lines, add them first
        if (summaryLines.length > 0) {
          parts.push(
            <p key={`summary-${parts.length}`} className="paddock-summary-text">
              {summaryLines.join(' ')}
            </p>
          );
          summaryLines = [];
        }
        
        // Add section header
        currentSection = line;
        parts.push(
          <h3 key={`section-${parts.length}`} className="paddock-section-header">
            {line}
          </h3>
        );
      } else if (line.includes(' - ')) {
        // This is a race entry: "Meeting - Horse Name - analysis"
        const match = line.match(/^(.+?)\s*-\s*(.+?)\s*-\s*(.+)$/);
        if (match) {
          const [, meeting, horseName, analysis] = match;
          parts.push(
            <div key={`race-${parts.length}`} className="paddock-race-entry">
              <span className="paddock-meeting-horse">
                <strong>{meeting.trim()}</strong> - <strong>{horseName.trim()}</strong>
              </span>
              {' '}
              <span className="paddock-analysis">{analysis.trim()}</span>
            </div>
          );
        } else {
          // Fallback: if pattern doesn't match, just show as bullet
          parts.push(
            <div key={`race-${parts.length}`} className="paddock-race-entry">
              {line}
            </div>
          );
        }
      } else if (currentSection === null) {
        // This is summary text (before any section headers)
        summaryLines.push(line);
      } else {
        // This is continuation text after a race entry or section header
        // Add it as a regular paragraph
        parts.push(
          <p key={`text-${parts.length}`} className="paddock-continuation-text">
            {line}
          </p>
        );
      }
    }
    
    // Add any remaining summary lines
    if (summaryLines.length > 0) {
      parts.push(
        <p key={`summary-final`} className="paddock-summary-text">
          {summaryLines.join(' ')}
        </p>
      );
    }
    
    return parts;
  };
  
  const formattedContent = formatPaddockText(text);
  
  return (
    <div className={`paddock-formatted-content ${expanded ? '' : 'paddock-content-collapsed'}`}>
      {formattedContent}
    </div>
  );
};

const ConnectBetfairPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, session } = useAuth();
  const [status, setStatus] = useState<BetfairStatus | null>(null);
  const [accountDetails, setAccountDetails] = useState<BetfairAccount | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingAccount, setLoadingAccount] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [disconnectNotice, setDisconnectNotice] = useState<string | null>(null);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [paddockSummary, setPaddockSummary] = useState<string | null>(null);
  const [loadingPaddock, setLoadingPaddock] = useState(false);
  const [bets, setBets] = useState<SettledBetWithRace[]>([]);
  const [loadingBets, setLoadingBets] = useState(false);
  const [currency, setCurrency] = useState('GBP');
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [aiMode, setAiMode] = useState<boolean>(false);
  const [togglingAiMode, setTogglingAiMode] = useState(false);
  const [aiModeError, setAiModeError] = useState<React.ReactNode>(null);
  const [showAiModeConfirm, setShowAiModeConfirm] = useState(false);
  const statusCheckRetryRef = useRef(0);

  const stakeMissingForAiModeMessage = useMemo(
    () => (
      <>
        You need to set your stake per point in{' '}
        <Link to="/settings" className="connect-betfair-ai-mode-settings-link">
          Settings
        </Link>{' '}
        before you can enable AI Mode.
      </>
    ),
    []
  );

  const loadStakePerPointFromProfile = useCallback(async (): Promise<number | null> => {
    if (!user?.id) return null;
    const profile = await fetchProfile(user.id);
    return profile?.default_stake ?? null;
  }, [user?.id]);

  useEffect(() => {
    let successTimeout: ReturnType<typeof setTimeout> | undefined;
    let redirectTimeout: ReturnType<typeof setTimeout> | undefined;

    // Check for callback parameters
    const urlParams = new URLSearchParams(window.location.search);
    const betfairConnected = urlParams.get('betfair_connected');
    const betfairError = urlParams.get('betfair_error');

    if (betfairConnected === 'true') {
      setSuccess(true);
      setDisconnectNotice(null);
      setError(null);
      // Clean up URL
      window.history.replaceState({}, document.title, '/dashboard');
      // Refresh status
      if (user?.id && session?.access_token) {
        loadStatus();
      }
      // Show success briefly, then route to dashboard to avoid callback-page dead-end.
      successTimeout = setTimeout(() => {
        setSuccess(false);
      }, 5000);
      redirectTimeout = setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } else if (betfairError) {
      setError(getErrorMessage(betfairError));
      setSuccess(false);
      // Clean up URL
      window.history.replaceState({}, document.title, '/dashboard');
    }

    // Load Betfair status first; paddock/performance load after status resolves (reduces API burst).
    if (user?.id && session?.access_token) {
      void loadStatus();
    } else {
      setLoading(false);
    }

    return () => {
      if (successTimeout) {
        clearTimeout(successTimeout);
      }
      if (redirectTimeout) {
        clearTimeout(redirectTimeout);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, session, navigate]);

  const loadDashboardExtras = () => {
    void loadPaddockSummary();
    if (user?.id) {
      void loadPerformanceData();
    }
  };

  const loadStatus = async () => {
    if (!user?.id || !session?.access_token) return;

    let keepLoading = false;
    try {
      setLoading(true);
      const connectionStatus = await checkBetfairStatus(user.id, session.access_token);

      if (connectionStatus.statusCheckFailed) {
        if (statusCheckRetryRef.current < 2) {
          statusCheckRetryRef.current += 1;
          keepLoading = true;
          window.setTimeout(() => {
            void loadStatus();
          }, 1000);
          return;
        }
        setStatus(connectionStatus);
        setError('Could not verify Betfair connection. Please try again in a moment.');
        loadDashboardExtras();
        return;
      }

      statusCheckRetryRef.current = 0;
      setStatus(connectionStatus);

      if (connectionStatus.connected) {
        setError(null);
        void loadAccountDetails();
      } else {
        setAccountDetails(null);
        if (connectionStatus.reauth_required) {
          setError('Your Betfair authorization has expired or been revoked. Please reconnect your account.');
        }
      }
      loadDashboardExtras();
    } catch (err) {
      console.error('Error loading status:', err);
      setError('Failed to load connection status');
      loadDashboardExtras();
    } finally {
      if (!keepLoading) {
        setLoading(false);
      }
    }
  };

  const loadAccountDetails = async () => {
    if (!user?.id || !session?.access_token) return;

    try {
      setLoadingAccount(true);
      const response = await getAccountDetails(user.id, session.access_token);
      setAccountDetails(response.account);
      // Update AI mode state from account details
      if (response.account.aiMode !== undefined) {
        setAiMode(response.account.aiMode);
      }
    } catch (err) {
      console.error('Error loading account details:', err);
      const message = err instanceof Error ? err.message : String(err);
      if (message.toLowerCase().includes('reconnect') || message.toLowerCase().includes('token expired')) {
        setStatus({
          connected: false,
          reauth_required: true,
          reason: 'revoked_or_invalid',
          status: 'disconnected',
        });
        setAccountDetails(null);
        setError('Your Betfair authorization has expired or been revoked. Please reconnect your account.');
      }
    } finally {
      setLoadingAccount(false);
    }
  };

  const loadPaddockSummary = async () => {
    try {
      setLoadingPaddock(true);
      const summary = await fetchTodaysPaddockSummary();
      setPaddockSummary(summary);
    } catch (err) {
      console.error('Error loading paddock summary:', err);
    } finally {
      setLoadingPaddock(false);
    }
  };

  const loadPerformanceData = async () => {
    if (!user?.id) return;

    try {
      setLoadingBets(true);
      
      // Load currency
      const profile = await fetchProfile(user.id);
      if (profile?.currency) {
        setCurrency(profile.currency);
      }

      // Load settled bets
      const settledBets = await fetchSettledBets(user.id);
      setBets(settledBets);
    } catch (err) {
      console.error('Error loading performance data:', err);
    } finally {
      setLoadingBets(false);
    }
  };

  const getCurrencySymbol = (curr: string): string => {
    const symbols: { [key: string]: string } = {
      'GBP': '£',
      'USD': '$',
      'EUR': '€',
      'AUD': 'A$',
      'CAD': 'C$'
    };
    return symbols[curr] || curr;
  };

  const currencySymbol = getCurrencySymbol(currency);

  // Helper function to calculate P/L for a bet based on status, stake, and odds
  const calculateBetPL = useCallback((bet: SettledBetWithRace): number => {
    // If pnl is already set and not null, use it
    if (bet.pnl !== null && bet.pnl !== undefined) {
      return Number(bet.pnl);
    }
    
    // Otherwise calculate based on status
    switch (bet.status) {
      case 'won':
        // Won: profit = (stake * odds) - stake = stake * (odds - 1)
        // Use potential_profit if available, otherwise calculate
        if (bet.potential_profit !== null && bet.potential_profit !== undefined) {
          return Number(bet.potential_profit);
        }
        return (Number(bet.stake) * Number(bet.odds)) - Number(bet.stake);
      case 'lost':
        // Lost: -stake
        return -Number(bet.stake);
      case 'void':
        // Void: stake returned, no profit/loss
        return 0;
      default:
        return 0;
    }
  }, []);

  // Calculate P/L for all bets first, then use those values for stats
  const betsWithPL = useMemo(() => {
    return bets.map(bet => {
      const betPL = calculateBetPL(bet);
      return {
        ...bet,
        calculatedPL: betPL
      };
    });
  }, [bets, calculateBetPL]);

  // Calculate all-time performance using the calculated P/L values
  const allTimeStats = useMemo(() => {
    const totalPL = betsWithPL.reduce((sum, bet) => sum + bet.calculatedPL, 0);
    const totalStaked = betsWithPL.reduce((sum, bet) => sum + bet.stake, 0);
    const roi = totalStaked > 0 ? (totalPL / totalStaked) * 100 : 0;
    const wins = betsWithPL.filter(bet => bet.status === 'won').length;
    const losses = betsWithPL.filter(bet => bet.status === 'lost').length;
    const winRate = betsWithPL.length > 0 ? (wins / betsWithPL.length) * 100 : 0;
    const avgOdds = betsWithPL.length > 0 
      ? betsWithPL.reduce((sum, bet) => sum + bet.odds, 0) / betsWithPL.length 
      : 0;
    const avgStake = betsWithPL.length > 0 
      ? betsWithPL.reduce((sum, bet) => sum + bet.stake, 0) / betsWithPL.length 
      : 0;

    return {
      netPL: totalPL,
      roi,
      totalBets: betsWithPL.length,
      winRate,
      avgOdds,
      avgStake,
      wins,
      losses
    };
  }, [betsWithPL]);

  const formatNumber = (num: number, decimals: number = 2): string => {
    return num.toFixed(decimals);
  };

  const formatPercent = (num: number, decimals: number = 2): string => {
    const sign = num >= 0 ? '+' : '';
    return `${sign}${num.toFixed(decimals)}%`;
  };

  const formatCurrency = (num: number): string => {
    const sign = num >= 0 ? '+' : '';
    return `${sign}${currencySymbol}${Math.abs(num).toFixed(2)}`;
  };

  const handleConnect = async () => {
    if (!user?.id) {
      setError('User not authenticated');
      return;
    }

    if (!session?.access_token) {
      setError('Session expired. Please log in again.');
      return;
    }

    try {
      setConnecting(true);
      setError(null);
      const authUrl = await initiateConnection(user.id, session.access_token);
      // Redirect to Betfair
      window.location.href = authUrl;
    } catch (err: any) {
      console.error('Error initiating connection:', err);
      setError(err.message || 'Failed to initiate connection');
      setConnecting(false);
    }
  };

  const handleDisconnectClick = () => {
    setShowDisconnectConfirm(true);
  };

  const handleDisconnectConfirm = async () => {
    if (!user?.id) {
      setError('User not authenticated');
      setShowDisconnectConfirm(false);
      return;
    }

    if (!session?.access_token) {
      setError('Session expired. Please log in again.');
      setShowDisconnectConfirm(false);
      return;
    }

    try {
      setDisconnecting(true);
      setError(null);
      setSuccess(false);
      setDisconnectNotice(null);
      setShowDisconnectConfirm(false);

      const disconnectResult = await disconnectBetfair(user.id, session.access_token);

      // Clear account details
      setAccountDetails(null);

      // Refresh status to show disconnected state
      await loadStatus();

      setSuccess(true);
      if (disconnectResult.warning && disconnectResult.betfair_revoked === false) {
        setDisconnectNotice(disconnectResult.warning);
      }
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 5000);
    } catch (err: any) {
      console.error('Error disconnecting:', err);
      setError(err.message || 'Failed to disconnect');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleDisconnectCancel = () => {
    setShowDisconnectConfirm(false);
  };

  const handleToggleAiMode = async () => {
    if (!user?.id || !session?.access_token) {
      setAiModeError('User not authenticated');
      return;
    }

    // If enabling, require stake per point (profile default_stake) then show confirmation
    if (!aiMode) {
      try {
        const stake = await loadStakePerPointFromProfile();
        if (stake == null) {
          setAiModeError(stakeMissingForAiModeMessage);
          return;
        }
      } catch {
        setAiModeError('Could not load your profile. Please try again.');
        return;
      }
      setAiModeError(null);
      setShowAiModeConfirm(true);
      return;
    }

    // If disabling, proceed directly
    await performToggleAiMode(false);
  };

  const performToggleAiMode = async (newAiMode: boolean) => {
    if (!user?.id || !session?.access_token) {
      setAiModeError('User not authenticated');
      return;
    }

    try {
      setTogglingAiMode(true);
      setAiModeError(null);
      if (newAiMode) {
        try {
          const stake = await loadStakePerPointFromProfile();
          if (stake == null) {
            setShowAiModeConfirm(false);
            setAiModeError(stakeMissingForAiModeMessage);
            return;
          }
        } catch {
          setShowAiModeConfirm(false);
          setAiModeError('Could not load your profile. Please try again.');
          return;
        }
      }
      setShowAiModeConfirm(false);
      await toggleAiMode(user.id, newAiMode, session.access_token);
      setAiMode(newAiMode);
      // Update account details to reflect the change
      if (accountDetails) {
        setAccountDetails({ ...accountDetails, aiMode: newAiMode });
      }
      // Refresh account details to clear cache (but don't show loading state)
      const response = await getAccountDetails(user.id, session.access_token, true);
      setAccountDetails(response.account);
      if (response.account.aiMode !== undefined) {
        setAiMode(response.account.aiMode);
      }
    } catch (err: any) {
      console.error('Error toggling AI mode:', err);
      setAiModeError(err.message || 'Failed to toggle AI mode');
    } finally {
      setTogglingAiMode(false);
    }
  };

  const handleAiModeConfirmCancel = () => {
    setShowAiModeConfirm(false);
  };

  const handleAiModeConfirmEnable = () => {
    performToggleAiMode(true);
  };

  const getErrorMessage = (errorCode: string): string => {
    const errorMessages: { [key: string]: string } = {
      'no_session_token': 'Session expired. Please log in again.',
      'invalid_state': 'Invalid connection request. Please try again.',
      'invalid_session': 'Invalid session. Please log in again.',
      'database_error': 'Database error. Please try again later.',
      'missing_parameters': 'Missing required parameters. Please try again.',
    };

    return errorMessages[errorCode] || `Connection error: ${errorCode}`;
  };

  if (loading) {
    return (
      <div className="connect-betfair-content">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="connect-betfair-content">
        <DashboardWelcomePanel betfairConnected={!!status?.connected} loading={loading} />

        {!status?.connected && !status?.statusCheckFailed && (
          <>
            <h1>Connect to Betfair</h1>
            <p className="connect-betfair-description">
              Connect your Betfair account to place bets directly on Betfair Exchange.
            </p>
          </>
        )}

        {status?.statusCheckFailed && (
          <>
            <h1>Betfair Account</h1>
            <p className="connect-betfair-description">
              We couldn&apos;t verify your Betfair connection just now. This is usually temporary.
            </p>
            <button
              type="button"
              className="connect-betfair-button connect-betfair-button-primary"
              onClick={() => {
                statusCheckRetryRef.current = 0;
                void loadStatus();
              }}
            >
              Retry connection check
            </button>
          </>
        )}
        
        {status?.connected && (
          <div className="connect-betfair-header-row">
            <h1>Betfair Account</h1>
            <button
              onClick={handleDisconnectClick}
              disabled={disconnecting}
              className="connect-betfair-button connect-betfair-button-disconnect connect-betfair-button-small"
            >
              {disconnecting ? 'Disconnecting...' : 'Disconnect'}
            </button>
          </div>
        )}

        {success && (
          <div className="connect-betfair-alert connect-betfair-success">
            <strong>Success!</strong>{' '}
            {status?.connected
              ? 'Your Betfair account has been connected successfully.'
              : 'Your Betfair account has been disconnected successfully.'}
          </div>
        )}

        {disconnectNotice && (
          <div className="connect-betfair-alert connect-betfair-warning" role="status">
            <strong>Note:</strong> {disconnectNotice}
          </div>
        )}

        {error && (
          <div className="connect-betfair-alert connect-betfair-error">
            <strong>Error:</strong> {error}
          </div>
        )}

        {status?.connected ? (
          <div className="connect-betfair-status">
            {/* Account Details Tiles */}
            {accountDetails && (
              <div className="connect-betfair-account-tiles">
                <div className="connect-betfair-tile">
                  <div className="connect-betfair-tile-icon">
                    <CheckCircle2 size={20} />
                  </div>
                  <div className="connect-betfair-tile-value connected-text">
                    {accountDetails.status ? accountDetails.status.charAt(0).toUpperCase() + accountDetails.status.slice(1).toLowerCase() : 'Connected'}
                  </div>
                  <div className="connect-betfair-tile-label">Status</div>
                </div>

                <div className="connect-betfair-tile">
                  <div className="connect-betfair-tile-icon">
                    <Wallet size={20} />
                  </div>
                  <div className="connect-betfair-tile-value">
                    £{accountDetails.availableToBetBalance?.toFixed(2) || '0.00'}
                  </div>
                  <div className="connect-betfair-tile-label">Available Balance</div>
                </div>

                <div className="connect-betfair-tile">
                  <div className="connect-betfair-tile-icon">
                    <TrendingUp size={20} />
                  </div>
                  <div className="connect-betfair-tile-value">
                    £{accountDetails.exposure?.toFixed(2) || '0.00'}
                  </div>
                  <div className="connect-betfair-tile-label">Exposure</div>
                </div>
              </div>
            )}

            {loadingAccount && (
              <div className="connect-betfair-loading-account">
                <LoadingSpinner />
                <p>Loading account details...</p>
              </div>
            )}

            {/* AI Mode Card */}
            {accountDetails && (
              <div className="connect-betfair-ai-mode-card">
                {togglingAiMode && (
                  <div className="connect-betfair-ai-mode-loading">
                    <LoadingSpinner />
                  </div>
                )}
                <div className="connect-betfair-ai-mode-icon">
                  <Bot size={32} />
                </div>
              <div className="connect-betfair-ai-mode-content">
                <div className="connect-betfair-ai-mode-title-row">
                  <div className="connect-betfair-ai-mode-title">AI Mode</div>
                  <div className={`connect-betfair-ai-mode-badge ${aiMode ? 'activated' : 'deactivated'}`}>
                    {aiMode ? 'ACTIVATED' : 'DE-ACTIVATED'}
                  </div>
                </div>
                <div className="connect-betfair-ai-mode-subtitle">
                  When activated, bets will be automatically be placed on picks between 11:00 am and 1:00 pm (UK Time) using your stake per point setting.
                </div>
                {aiModeError && (
                  <div className="connect-betfair-ai-mode-error">
                    {aiModeError}
                  </div>
                )}
              </div>
                <div className="connect-betfair-ai-mode-toggle-container">
                  <button
                    className={`connect-betfair-ai-mode-toggle ${aiMode ? 'active' : ''}`}
                    onClick={handleToggleAiMode}
                    disabled={togglingAiMode}
                    aria-label={aiMode ? 'Disable AI Mode' : 'Enable AI Mode'}
                  >
                    <span className="connect-betfair-ai-mode-toggle-slider"></span>
                  </button>
                </div>
              </div>
            )}

            {/* Performance Section */}
            <div className="connect-betfair-performance-section">
              <h2 className="connect-betfair-section-title">Performance</h2>
              {loadingBets ? (
                <div className="connect-betfair-loading-account">
                  <LoadingSpinner />
                  <p>Loading performance data...</p>
                </div>
              ) : (
                <div className="connect-betfair-performance-tiles">
                  <div className={`connect-betfair-summary-tile ${allTimeStats.netPL >= 0 ? 'profit' : 'loss'}`}>
                    <div className="connect-betfair-summary-icon">
                      {allTimeStats.netPL >= 0 ? <TrendingUp size={24} /> : <TrendingDown size={24} />}
                    </div>
                    <div className="connect-betfair-summary-value">{formatCurrency(allTimeStats.netPL)}</div>
                    <div className="connect-betfair-summary-label">Net P/L</div>
                  </div>
                  <div className={`connect-betfair-summary-tile ${allTimeStats.roi >= 0 ? 'profit' : 'loss'}`}>
                    <div className="connect-betfair-summary-icon">
                      <Percent size={24} />
                    </div>
                    <div className="connect-betfair-summary-value">{formatPercent(allTimeStats.roi)}</div>
                    <div className="connect-betfair-summary-label">ROI</div>
                  </div>
                  <div className="connect-betfair-summary-tile">
                    <div className="connect-betfair-summary-icon">
                      <Hash size={24} />
                    </div>
                    <div className="connect-betfair-summary-value">{allTimeStats.totalBets}</div>
                    <div className="connect-betfair-summary-label">Bets</div>
                  </div>
                  <div className="connect-betfair-summary-tile">
                    <div className="connect-betfair-summary-icon">
                      <Target size={24} />
                    </div>
                    <div className="connect-betfair-summary-value">{formatPercent(allTimeStats.winRate)}</div>
                    <div className="connect-betfair-summary-label">Win Rate</div>
                  </div>
                  <div className="connect-betfair-summary-tile">
                    <div className="connect-betfair-summary-icon">
                      <Trophy size={24} />
                    </div>
                    <div className="connect-betfair-summary-value">{formatNumber(allTimeStats.avgOdds, 2)}</div>
                    <div className="connect-betfair-summary-label">Avg Odds</div>
                  </div>
                  <div className="connect-betfair-summary-tile">
                    <div className="connect-betfair-summary-icon">
                      <DollarSign size={24} />
                    </div>
                    <div className="connect-betfair-summary-value">{currencySymbol}{formatNumber(allTimeStats.avgStake, 2)}</div>
                    <div className="connect-betfair-summary-label">Avg Stake</div>
                  </div>
                  <div className="connect-betfair-summary-tile won">
                    <div className="connect-betfair-summary-icon">
                      <Check size={24} />
                    </div>
                    <div className="connect-betfair-summary-value">{allTimeStats.wins}</div>
                    <div className="connect-betfair-summary-label">Wins</div>
                  </div>
                  <div className="connect-betfair-summary-tile lost">
                    <div className="connect-betfair-summary-icon">
                      <X size={24} />
                    </div>
                    <div className="connect-betfair-summary-value">{allTimeStats.losses}</div>
                    <div className="connect-betfair-summary-label">Losses</div>
                  </div>
                </div>
              )}
            </div>

            {/* Notes from the Paddock Section */}
            <div className="connect-betfair-notes-card">
              <div className="connect-betfair-notes-header">
                <h2 className="connect-betfair-notes-title">Notes from the Paddock</h2>
                {paddockSummary && !loadingPaddock && (
                  <button
                    className="connect-betfair-read-more-button"
                    onClick={() => setNotesExpanded(!notesExpanded)}
                  >
                    {notesExpanded ? 'Show Less' : 'Read More'}
                  </button>
                )}
              </div>
              <div className="connect-betfair-notes-content">
                {loadingPaddock ? (
                  <LoadingSpinner />
                ) : paddockSummary ? (
                  <FormattedPaddockSummary text={paddockSummary} expanded={notesExpanded} />
                ) : (
                  <p style={{ color: '#94a3b8', fontStyle: 'italic' }}>
                    No notes available for today.
                  </p>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="connect-betfair-actions">
            <button
              onClick={handleConnect}
              disabled={connecting}
              className="connect-betfair-button"
            >
              {connecting ? 'Connecting...' : 'Connect to Betfair'}
            </button>
          </div>
        )}

        {/* Disconnect Confirmation Dialog */}
        {showDisconnectConfirm && (
          <div className="connect-betfair-modal-overlay" onClick={handleDisconnectCancel}>
            <div className="connect-betfair-modal" onClick={(e) => e.stopPropagation()}>
              <div className="connect-betfair-modal-header">
                <h3>Disconnect Betfair Account?</h3>
              </div>
              <div className="connect-betfair-modal-body">
                <p>
                  Are you sure you want to disconnect your Betfair account? This will revoke access 
                  and you will need to reconnect to use Betfair features.
                </p>
              </div>
              <div className="connect-betfair-modal-actions">
                <button
                  onClick={handleDisconnectCancel}
                  className="connect-betfair-modal-button connect-betfair-modal-button-cancel"
                  disabled={disconnecting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDisconnectConfirm}
                  className="connect-betfair-modal-button connect-betfair-modal-button-confirm"
                  disabled={disconnecting}
                >
                  {disconnecting ? 'Disconnecting...' : 'Disconnect'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* AI Mode Enable Confirmation Dialog */}
        {showAiModeConfirm && (
          <div className="connect-betfair-modal-overlay" onClick={handleAiModeConfirmCancel}>
            <div className="connect-betfair-modal connect-betfair-ai-mode-modal" onClick={(e) => e.stopPropagation()}>
              <div className="connect-betfair-modal-header">
                <h3>Are you sure you want to Enable AI Mode?</h3>
              </div>
              <div className="connect-betfair-modal-body">
                <div className="connect-betfair-ai-mode-info">
                  <div className="connect-betfair-ai-mode-info-line">
                    <strong>Bet Placement Time:</strong>{' '}
                    <span>When enabled, bets will automatically start being placed from 11:00 am (UK Time) on ALL picks for the day.</span>
                  </div>
                  <div className="connect-betfair-ai-mode-info-line">
                    <strong>Stake Calculation:</strong>{' '}
                    <span>
                      Stakes will be calculated using your stake per point. Please ensure you have assigned your stake per point in{' '}
                      <a 
                        href="/settings" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="connect-betfair-ai-mode-settings-link"
                      >
                        settings
                      </a>
                      {' '}and that you have sufficient balance in your Betfair Exchange wallet.
                    </span>
                  </div>
                </div>
              </div>
              <div className="connect-betfair-modal-actions">
                <button
                  onClick={handleAiModeConfirmCancel}
                  className="connect-betfair-modal-button connect-betfair-modal-button-cancel"
                  disabled={togglingAiMode}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAiModeConfirmEnable}
                  className="connect-betfair-modal-button connect-betfair-modal-button-confirm"
                  disabled={togglingAiMode}
                >
                  {togglingAiMode ? 'Enabling...' : 'Enable'}
                </button>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default ConnectBetfairPage;

